import {
  forEachRight,
  get,
  isEqual,
  isFinite,
  isNumber,
  lt,
  lte,
  size,
  slice,
  split,
  toString,
  trimEnd,
} from 'lodash';
import { decimalNumber } from '../common/decimal-number/decimal-number';
import { DEFAULT_NUMBER_PRECISION } from '../services/number-format/number-format.constants';

/**
 * data parsed to value
 * if value is not a finite primitive number, will use default value
 *
 * @param value base value
 * @param defaultValue default value
 * @returns parsed float value
 */
const parseToFloat = (value: string, defaultValue: number = 0): number => {
  const parsedValue = parseFloat(value);

  return isFinite(parsedValue) ? parsedValue : defaultValue;
};

/**
 * judge is valid number
 * - is number
 * - is more or equal to lowest value
 *
 * @param value target value
 * @param lowestValue lowest value
 * @returns is valid Number
 */
const isValidNumber = (value: number, lowestValue: number = 0): boolean =>
  isNumber(value) && value >= lowestValue;

/**
 * @desc remove no meaningful decimals.
 * @param value stringified number
 * @param minDecimalPlacesToCheck min decimal places which no need to check
 * @returns  meaningful stringified number after remove
 */
const removeInvalidZeros = (
  value: string,
  minDecimalPlacesToCheck: number = DEFAULT_NUMBER_PRECISION,
): string => {
  if (decimalNumber(value).isNaN) return value;

  const [receivedIntPart, receivedDecimalsPart] = split(value, '.');

  let keepDecimalIndex = minDecimalPlacesToCheck;

  forEachRight(receivedDecimalsPart, (decimalValue, index) => {
    // we need break out this loop when
    // less or equal to check min decimal places, or decimal value is valid number(not 0);
    const isContinueLoop =
      lte(index, minDecimalPlacesToCheck - 1) || !isEqual(decimalValue, '0');
    keepDecimalIndex = isContinueLoop ? index + 1 : keepDecimalIndex;

    return !isContinueLoop;
  });

  // combine int part and decimal part which removed invalid zeros
  const removedInvalidZeroValue = `${receivedIntPart}.${slice(receivedDecimalsPart, 0, keepDecimalIndex).join('')}`;

  // trim end '.' to avoid pass value like '2.'
  return trimEnd(removedInvalidZeroValue, '.');
};

const generateMaximumByPreferencesPrecision = (
  maxInt: number,
  configPrecision: number = DEFAULT_NUMBER_PRECISION,
): number | null => {
  if (!maxInt) return null;
  let decimalStrPart: string = '';
  let decimalLooper: number = configPrecision;
  const isNegative: boolean = lt(maxInt, 0);
  while (decimalLooper) {
    decimalStrPart += '9';
    decimalLooper -= 1;
  }
  const decimalIntPart: number =
    Number(decimalStrPart) / Math.pow(10, configPrecision);
  if (isNegative) {
    return -(-maxInt + decimalIntPart);
  }

  return maxInt + decimalIntPart;
};

/**
 *
 * @param receivedNumber 9.99 0.01
 * @returns precision places 9.99 as 2; 0.0001 as 4;
 */
const getDecimalPlaces = (receivedNumber: number): number =>
  size(get(split(toString(receivedNumber), '.'), 1));

export {
  parseToFloat,
  isValidNumber,
  removeInvalidZeros,
  generateMaximumByPreferencesPrecision,
  getDecimalPlaces,
};
